import * as React from "react";
import { Card, CardBody, PageContainer } from "../components/Card";
import { PageHeader } from "../components/PageHeader";
import { Button, IconButton } from "../components/Button";
import { Modal, ModalBody } from "../components/Modal";
import { ConfirmModal } from "../components/ConfirmModal";
import { Field, Input } from "../components/Form";
import { StatusBadge } from "../components/Badge";
import { Banner, EmptyState, ListStates } from "../components/States";
import { SearchInput, Toolbar } from "../components/Toolbar";
import { Icon } from "../components/Icon";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { ProcedureModel } from "../api/APIClient";
import { ProcedureEditor } from "./ProcedureEditor";
import * as Store from "./ProceduresStore";

/**
 * Calculation procedures: a list on the left, the selected one's editor on the right.
 *
 * A procedure is a stable family; its versions carry the actual rules. The list here is the family
 * level - create, rename, archive - and everything about a version happens in the editor.
 */

export const ProceduresIndex: React.FC = () => {
    const toast = useToast();
    const canWrite = usePermission(Permissions.PROCEDURES_WRITE);

    const [query, setQuery] = React.useState("");
    const [selectedID, setSelectedID] = React.useState("");
    const [creating, setCreating] = React.useState(false);
    const [renaming, setRenaming] = React.useState<ProcedureModel | null>(null);
    const [archiving, setArchiving] = React.useState<ProcedureModel | null>(null);

    const procedures = useAsync(() => Store.listProcedures(true), [], { initial: [] as ProcedureModel[] });
    const rows = procedures.data ?? [];

    const filtered = React.useMemo(() => {
        const needle = query.trim().toLowerCase();
        return needle ? rows.filter((p) => (p.name ?? "").toLowerCase().includes(needle)) : rows;
    }, [rows, query]);

    const selected = rows.find((p) => p.id === selectedID);

    // Select the first procedure once the list arrives, so the editor is not empty for no reason.
    React.useEffect(() => {
        if (!selectedID && filtered.length) setSelectedID(filtered[0].id ?? "");
    }, [filtered, selectedID]);

    const archive = useAction(async () => {
        if (!archiving) return;
        await Store.archiveProcedure(archiving.id ?? "");
        toast.success("Procedure archived.");
        setArchiving(null);
        procedures.refresh();
    });

    return (
        <PageContainer>
            <PageHeader
                title="Calculation procedures"
                icon={["fad", "diagram-project"]}
                description="The rules that turn marks into other marks. Author a draft, validate it, publish it, then assign the published version to a module."
                actions={
                    canWrite && (
                        <Button icon={["fad", "plus"]} onClick={() => setCreating(true)}>
                            New procedure
                        </Button>
                    )
                }
            />

            <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
                <div>
                    <Toolbar>
                        <SearchInput value={query} onChange={setQuery} placeholder="Search procedures..." />
                    </Toolbar>

                    <Card>
                        <ListStates
                            isLoading={procedures.isLoading}
                            error={procedures.error}
                            onRetry={procedures.refresh}
                            isEmpty={filtered.length === 0}
                            empty={{
                                icon: ["fad", "diagram-project"],
                                title: query ? "No procedures match" : "No procedures yet",
                                hint: query ? "Try a different search." : canWrite ? "Create one to get started." : undefined,
                            }}
                        >
                            <ul className="divide-y divide-border">
                                {filtered.map((procedure) => (
                                    <li key={procedure.id}>
                                        <div
                                            className={`flex items-center gap-2 px-4 py-3 transition-colors ${
                                                procedure.id === selectedID ? "bg-primary-soft" : "hover:bg-surface-hover"
                                            }`}
                                        >
                                            <button
                                                type="button"
                                                className="min-w-0 flex-1 text-left"
                                                onClick={() => setSelectedID(procedure.id ?? "")}
                                            >
                                                <span
                                                    className={`block truncate text-sm font-medium ${
                                                        procedure.id === selectedID ? "text-primary-text" : "text-text-strong"
                                                    }`}
                                                >
                                                    {procedure.name}
                                                </span>
                                                {procedure.isArchived && <StatusBadge status="Archive" />}
                                            </button>

                                            {canWrite && (
                                                <div className="flex items-center gap-1">
                                                    <IconButton
                                                        icon={["fad", "pen"]}
                                                        label={`Rename ${procedure.name}`}
                                                        onClick={() => setRenaming(procedure)}
                                                    />
                                                    {!procedure.isArchived && (
                                                        <IconButton
                                                            icon={["fad", "box-archive"]}
                                                            label={`Archive ${procedure.name}`}
                                                            onClick={() => setArchiving(procedure)}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </ListStates>
                    </Card>
                </div>

                <div>
                    {selected ? (
                        <>
                            <div className="mb-4 flex items-center gap-2">
                                <Icon icon={["fad", "diagram-project"]} className="text-primary" />
                                <h2 className="text-lg font-semibold text-text-strong">{selected.name}</h2>
                                {selected.isArchived && <StatusBadge status="Archive" />}
                            </div>

                            {selected.isArchived && (
                                <Banner tone="warning" className="mb-4">
                                    This procedure is archived. Existing assignments keep working; it just will not be offered for new
                                    ones.
                                </Banner>
                            )}

                            <ProcedureEditor key={selected.id} procedure={selected} onChanged={procedures.refresh} />
                        </>
                    ) : (
                        <Card>
                            <EmptyState icon={["fad", "diagram-project"]} title="Select a procedure" hint="Or create one to get started." />
                        </Card>
                    )}
                </div>
            </div>

            <NameModal
                isOpen={creating}
                title="New procedure"
                label="Name"
                placeholder="Term mark from division marks"
                initial=""
                confirmLabel="Create"
                onClose={() => setCreating(false)}
                onSubmit={async (name) => {
                    const created = await Store.createProcedure(name);
                    toast.success("Procedure created.");
                    setCreating(false);
                    procedures.refresh();
                    const id = (created as any)?.procedure?.id ?? (created as any)?.id;
                    if (id) setSelectedID(id);
                }}
            />

            <NameModal
                isOpen={renaming !== null}
                title="Rename procedure"
                label="Name"
                placeholder=""
                initial={renaming?.name ?? ""}
                confirmLabel="Save"
                onClose={() => setRenaming(null)}
                onSubmit={async (name) => {
                    await Store.renameProcedure(renaming?.id ?? "", name);
                    toast.success("Procedure renamed.");
                    setRenaming(null);
                    procedures.refresh();
                }}
            />

            <ConfirmModal
                isOpen={archiving !== null}
                onClose={() => setArchiving(null)}
                onConfirm={archive.run}
                title="Archive procedure"
                icon={["fad", "box-archive"]}
                confirmVariant="warning"
                confirmLabel="Archive"
                busy={archive.busy}
                error={archive.error}
                body={
                    <>
                        Archive <strong>{archiving?.name}</strong>? Existing assignments keep running - it is only hidden from new ones.
                    </>
                }
            />
        </PageContainer>
    );
};

/** Small single-field modal, shared by create and rename. */
const NameModal: React.FC<{
    isOpen: boolean;
    title: string;
    label: string;
    placeholder: string;
    initial: string;
    confirmLabel: string;
    onClose: () => void;
    onSubmit: (value: string) => Promise<void>;
}> = ({ isOpen, title, label, placeholder, initial, confirmLabel, onClose, onSubmit }) => {
    const [value, setValue] = React.useState(initial);

    React.useEffect(() => {
        if (isOpen) setValue(initial);
    }, [isOpen, initial]);

    const submit = useAction(() => onSubmit(value.trim()));

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            icon={["fad", "diagram-project"]}
            size="sm"
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={submit.busy}>
                        Cancel
                    </Button>
                    <Button onClick={submit.run} loading={submit.busy} disabled={!value.trim()}>
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            <ModalBody>
                <Field label={label} required>
                    <Input value={value} placeholder={placeholder} autoFocus onChange={(e) => setValue(e.target.value)} />
                </Field>
                {submit.error && <Banner tone="danger">{submit.error}</Banner>}
            </ModalBody>
        </Modal>
    );
};

export default ProceduresIndex;
